import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import ScrollToTop from './components/ScrollToTop';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import Activities from './pages/Activities';
import ActivityDetail from './pages/ActivityDetail';
import Resources from './pages/Resources';
import SelfImprovement from './pages/SelfImprovement';
import Coaching from './pages/Coaching';
import Book from './pages/Book';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Contact from './pages/Contact';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import Favorites from './pages/Favorites';
import Profile from './pages/Profile';
import Orders from './pages/Orders';
import Downloads from './pages/Downloads';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import About from './pages/About';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminActivities from './pages/admin/AdminActivities';
import AdminResources from './pages/admin/AdminResources';
import AdminSelfImprovement from './pages/admin/AdminSelfImprovement';
import AdminContacts from './pages/admin/AdminContacts';
import AdminPricing from './pages/admin/AdminPricing';
import AdminSales from './pages/admin/AdminSales';
import AdminDigitalProducts from './pages/admin/AdminDigitalProducts';
import AdminHomepage from './pages/admin/AdminHomepage';
import AdminAbout from './pages/admin/AdminAbout';

// Protected route wrapper
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" />;
  }

  return children;
};

function AppContent() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Public routes with header/footer */}
          <Route path="/" element={
            <>
              <Header />
              <Home />
              <Footer />
            </>
          } />
          <Route path="/activities" element={
            <>
              <Header />
              <Activities />
              <Footer />
            </>
          } />
          <Route path="/activity/:id" element={
            <>
              <Header />
              <ActivityDetail />
              <Footer />
            </>
          } />
          <Route path="/resources" element={
            <>
              <Header />
              <Resources />
              <Footer />
            </>
          } />
          <Route path="/self-improvement" element={
            <>
              <Header />
              <SelfImprovement />
              <Footer />
            </>
          } />
          <Route path="/coaching" element={
            <>
              <Header />
              <Coaching />
              <Footer />
            </>
          } />
          <Route path="/book" element={
            <>
              <Header />
              <Book />
              <Footer />
            </>
          } />
          <Route path="/about" element={
            <>
              <Header />
              <About />
              <Footer />
            </>
          } />
          <Route path="/contact" element={
            <>
              <Header />
              <Contact />
              <Footer />
            </>
          } />
          
          {/* Auth routes without header/footer */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Payment routes */}
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          
          {/* User routes */}
          <Route path="/favorites" element={
            <>
              <Header />
              <Favorites />
              <Footer />
            </>
          } />
          <Route path="/profile" element={
            <>
              <Header />
              <Profile />
              <Footer />
            </>
          } />
          <Route path="/orders" element={
            <>
              <Header />
              <Orders />
              <Footer />
            </>
          } />
          <Route path="/downloads" element={
            <>
              <Header />
              <Downloads />
              <Footer />
            </>
          } />
          
          {/* Admin routes */}
          <Route path="/admin" element={
            <ProtectedRoute adminOnly={true}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/activities" element={
            <ProtectedRoute adminOnly={true}>
              <AdminActivities />
            </ProtectedRoute>
          } />
          <Route path="/admin/resources" element={
            <ProtectedRoute adminOnly={true}>
              <AdminResources />
            </ProtectedRoute>
          } />
          <Route path="/admin/self-improvement" element={
            <ProtectedRoute adminOnly={true}>
              <AdminSelfImprovement />
            </ProtectedRoute>
          } />
          <Route path="/admin/pricing" element={
            <ProtectedRoute adminOnly={true}>
              <AdminPricing />
            </ProtectedRoute>
          } />
          <Route path="/admin/contacts" element={
            <ProtectedRoute adminOnly={true}>
              <AdminContacts />
            </ProtectedRoute>
          } />
          <Route path="/admin/sales" element={
            <ProtectedRoute adminOnly={true}>
              <AdminSales />
            </ProtectedRoute>
          } />
          <Route path="/admin/digital-products" element={
            <ProtectedRoute adminOnly={true}>
              <AdminDigitalProducts />
            </ProtectedRoute>
          } />
          <Route path="/admin/homepage" element={
            <ProtectedRoute adminOnly={true}>
              <AdminHomepage />
            </ProtectedRoute>
          } />
          <Route path="/admin/about" element={
            <ProtectedRoute adminOnly={true}>
              <AdminAbout />
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;